import { useSearchDispatch, useSearchStoreContext } from "PFApp/search/search_context";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useWindowSize } from "PFCore/helpers/use_window_size";
import { useSynchronizedState } from "PFCore/hooks/use_synchronized_state";
import { EventKey } from "PFCore/types/event_key";
import { SEARCH_ACTIONS } from "PFReducers/search_reducer/search_reducer";
import { Importance } from "PFTypes";
import { CustomType } from "PFTypes";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { matchesTab, modalFakeTab } from "../tabs_constants";
import css from "./search_box.module.scss";

type SearchBoxProps = {
  buttonHref?: string;
};

/* eslint-disable jsx-a11y/no-autofocus */
export const SearchBox = ({ buttonHref }: SearchBoxProps) => {
  const {
    search: {
      tab,
      term,
      choosenKeywords,
      suggestedKeywords,
      trackKeywordsIds,
      autoselectEnabled,
      matchTypes,
      filterTypes,
      matchesPending,
      profilesPending,
      activitiesPending,
      searchActivityId
    }
  } = useSearchStoreContext();
  const dispatch = useSearchDispatch();
  const { t } = useTranslation("search");
  const navigate = useNavigate();

  const [localTerm, setLocalTerm] = useSynchronizedState(term ?? "");

  const availableSearchables = useMemo<CustomType[]>(
    () =>
      [...matchTypes, ...filterTypes]
        // We want to at max show 5 custom fields, so we randomise the list, and pick the first 5
        .sort(() => 0.5 - Math.random())
        .slice(0, 5),
    [matchTypes, filterTypes]
  );

  // We always want to show skills in the search bar
  const displaySearchText = availableSearchables.map((customType) => customType.display_as).join(" / ");

  useEffect(() => {
    if (autoselectEnabled) {
      const type = SEARCH_ACTIONS.SEARCH_PUSH_CHOOSEN_KEYWORD;
      suggestedKeywords
        .filter(({ autoSelect }) => autoSelect)
        .filter(({ id }) => !trackKeywordsIds.includes(Number(id)))
        .filter(({ value }) => value?.length > 2)
        .forEach((keyword) => dispatch({ type, payload: { ...keyword, importance: Importance.Essential } }));
    }
  }, [JSON.stringify(suggestedKeywords)]);

  const showKeywords =
    term !== "" &&
    (tab === matchesTab || tab === modalFakeTab) &&
    (choosenKeywords.length > 0 || suggestedKeywords.length > 0);

  const handleTermChange = () => {
    dispatch({ type: SEARCH_ACTIONS.SEARCH_MERGE_STATE, payload: { autoselectEnabled: true } });
    dispatch({ type: SEARCH_ACTIONS.SEARCH_SET_TERM, payload: localTerm });

    if (localTerm) {
      const termOrder = {
        direction: "desc",
        name: ["scores.term_score"],
        text: t("searchBox.mostRelevant")
      };
      dispatch({
        type: SEARCH_ACTIONS.SEARCH_MERGE_PROFILES_REQUEST_ORDER,
        payload: termOrder
      });
      dispatch({
        type: SEARCH_ACTIONS.SEARCH_MERGE_ACTIVITIES_REQUEST_ORDER,
        payload: termOrder
      });
    }
  };

  const debouncedHandleTermChange = useDebounce(handleTermChange, 1000);

  const handleChange = (event) => {
    setLocalTerm(event.target.value);
    debouncedHandleTermChange();
  };

  const { windowWidth } = useWindowSize();

  const disableSearch =
    localTerm.trim() === "" ||
    (!!searchActivityId && (matchesPending || profilesPending || activitiesPending));

  const triggerSubmit = buttonHref
    ? () => navigate(buttonHref)
    : (event) => {
        event.preventDefault();
        dispatch({ type: SEARCH_ACTIONS.SEARCH_TRIGGER });
      };

  const handleKeyDown = (event) => {
    if (!disableSearch && event.key === EventKey.Enter) {
      handleTermChange();
      triggerSubmit(event);
    }
  };

  return (
    <label
      className={css.box}
      style={{ marginBottom: showKeywords ? 0 : 20 }}
      data-qa-id="SearchBox"
      role="search"
    >
      <Icon name="search" size="lg" />

      <div className={css.input}>
        <input
          autoFocus
          value={localTerm}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            windowWidth > 700
              ? t("searchBox.searchByText", { displayText: displaySearchText })
              : t("searchBox.searchByTextShort", { displayText: displaySearchText })
          }
        />
      </div>

      <Button onKeyDown={handleKeyDown} onClick={triggerSubmit} disabled={disableSearch}>
        {t("searchBox.search")}
      </Button>
    </label>
  );
};
